/**
 * A phone number: the country, then the number.
 *
 * One box invites `0803…`, which is fine until the message is sent from a gateway that dials
 * internationally and the leading zero makes it unreachable. Two fields make the country explicit
 * and let the number be typed the way it is written down locally.
 *
 * **One column still.** What is stored is a single string, `+234 8031234567`, so an export, a
 * receipt and a database console all show a number somebody can ring without a lookup. The split
 * exists only for the length of the form: `splitPhone` on open, `joinPhone` on save, both in
 * `packages/core/src/countries.ts` and tested there.
 *
 * **The flag is artwork, not an emoji.** `🇳🇬` is two letters a font is meant to draw as a flag,
 * and Windows ships no font that does — so on most desktops the emoji renders as the letters
 * `NG` and the control loses the thing it was recognised by. See `components/flag.tsx`.
 */

import { useMemo, useState } from "react";
import {
  DEFAULT_COUNTRY,
  countryCode,
  detectedCountry,
  dialCode,
  diallingCountry,
  joinPhone,
  searchDiallingCountries,
  splitPhone,
} from "@rentorng/core";
import { SearchSelect, type Option } from "./search-select";
import { Flag } from "./flag";

const optionFor = (code: string, name: string, dial: string): Option => ({
  value: code,
  label: name,
  tag: `+${dial}`,
  icon: <Flag country={code} className="h-3.5 w-5" />,
});

/**
 * The list, with the country already chosen at the top of it.
 *
 * Opening on Afghanistan every time is a scroll for everybody. When nothing has been typed the
 * current choice leads, because the commonest reason for opening this list is to check what it
 * says rather than to change it.
 */
const searchFrom =
  (current: string) =>
  async (query: string): Promise<Option[]> => {
    const found = searchDiallingCountries(query, 60);
    const ordered = query.trim()
      ? found
      : [...found.filter((c) => c.code === current), ...found.filter((c) => c.code !== current)];
    return ordered.map((country) => optionFor(country.code, country.name, country.dial));
  };

/**
 * Which country to open on.
 *
 * In order: the saved number, because it is a fact rather than a guess; then the agency's own
 * country; then the machine's timezone; then the default. Nothing here is ever forced onto a
 * saved record — a number that arrived without a dialling code keeps exactly the digits it had.
 *
 * The browser's *language* is deliberately not consulted. An agency in Lagos running Windows in
 * English reports `en-US`, and opening on `+1` because of that is wrong for every number it will
 * ever enter.
 */
function detectCountry(orgCountry?: string | null): string {
  const own = countryCode(orgCountry);
  if (own) return own;

  const detected = detectedCountry();
  if (detected && dialCode(detected)) return detected;

  return countryCode(DEFAULT_COUNTRY) ?? "NG";
}

export function PhoneInput({
  onValue,
  name,
  id,
  defaultValue,
  orgCountry,
  required,
  disabled,
  placeholder = "803 123 4567",
  autoComplete = "tel",
}: {
  name?: string;
  id?: string;
  /** The stored number, as one string. */
  defaultValue?: string | null;
  /** The agency's country, used when there is nothing saved to read a country from. */
  orgCountry?: string | null;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  autoComplete?: string;
  /**
   * Told the joined value on every change.
   *
   * For callers that hold a list of numbers rather than one form field — the hidden input only
   * helps a form that reads `FormData`, and a repeatable row is state.
   */
  onValue?: (value: string) => void;
}) {
  const parts = useMemo(() => splitPhone(defaultValue), [defaultValue]);
  const [country, setCountry] = useState<string>(parts.country ?? detectCountry(orgCountry));
  const [number, setNumber] = useState<string>(parts.number);

  // Reported on every edit, so a parent holding a list stays in step with what is typed.
  const report = (nextCountry: string, nextNumber: string) => onValue?.(joinPhone(nextCountry, nextNumber));

  const dial = dialCode(country);
  const selected = useMemo<Option | null>(() => {
    const match = diallingCountry(country);
    return match ? optionFor(match.code, match.name, match.dial) : null;
  }, [country]);

  return (
    <div className="flex gap-2">
      {/* What is submitted. The two visible fields are only how it is typed. */}
      <input type="hidden" name={name} value={joinPhone(country, number)} />

      <SearchSelect
        value={country}
        onChange={(next) => {
          const chosen = next || country;
          setCountry(chosen);
          report(chosen, number);
        }}
        search={searchFrom(country)}
        selectedOption={selected}
        required
        disabled={disabled}
        className="w-[7.5rem] shrink-0"
        menuClassName="w-[20rem]"
        emptyMessage="No country matched."
        renderValue={() => (
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Flag country={country} className="h-3.5 w-5" />
            <span className="text-ink-700">{dial ? `+${dial}` : country}</span>
          </span>
        )}
      />

      <input
        id={id}
        // Not a `tel` input: on a phone that keyboard is right, but the control also has to reject
        // the letters a paste can bring in, which is easier done here than argued with the OS.
        inputMode="tel"
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        value={number}
        onChange={(event) => {
          const cleaned = event.target.value.replace(/[^\d\s()-]/g, "");
          setNumber(cleaned);
          report(country, cleaned);
        }}
        className="field min-w-0 flex-1"
      />
    </div>
  );
}
